import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { TrendingUp, TrendingDown, Minus, History, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * iter-221i — Listing Price History Panel
 * ─────────────────────────────────────────
 * Drops onto any listing detail page (Product/Service/Rental/Event/etc).
 * Shows the rolling 90-day base/aniyor/MRP-price ledger so vendors and
 * admins can answer "what was this priced at on date X" without paging
 * through Mongo.
 *
 * Hidden when there are zero recorded changes — keeps the UI clean for
 * stable listings.
 *
 * Props:
 *   listingId       (string, required) — listing.id
 *   defaultDays     (number, optional, default 90)
 *   className       (string, optional)
 *   alwaysShow      (bool, optional) — if true, show empty state instead
 *                                      of hiding when no rows.
 */
const fmtINR = (v) =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
      }).format(Number(v));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const SOURCE_LABELS = {
  vendor_edit: 'Vendor edit',
  admin_edit: 'Admin edit',
  revision_approval: 'Revision approved',
  csv_import: 'Bulk CSV import',
  revert_price_inflation: 'Inflation revert',
  rental_rate_migration: 'Rental migration',
  listing_created: 'Listing created',
  system: 'System',
};

const DeltaPill = ({ pct }) => {
  if (pct === null || pct === undefined || pct === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Minus className="w-3 h-3" /> 0.00%
      </span>
    );
  }
  const up = pct > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        up ? 'text-rose-600' : 'text-emerald-600'
      }`}
      data-testid="price-history-delta-pill"
    >
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}
      {pct.toFixed(2)}%
    </span>
  );
};

export const PriceHistoryPanel = ({
  listingId,
  defaultDays = 90,
  className = '',
  alwaysShow = false,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(defaultDays);

  const fetchHistory = async () => {
    if (!listingId) return;
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/listings/${listingId}/price-history?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        if (res.status === 404 || res.status === 403) {
          setData(null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      toast({
        title: 'Could not load price history',
        description: String(e.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [listingId, days]); // eslint-disable-line

  // Hide entirely when stable & not forced
  if (!loading && !alwaysShow && (!data || (data.history_count ?? 0) === 0)) {
    return null;
  }

  return (
    <Card className={className} data-testid="price-history-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-4 h-4" />
          Price history
          {data?.history_count > 0 && (
            <Badge variant="secondary" data-testid="price-history-count">
              {data.history_count} change{data.history_count !== 1 ? 's' : ''} · last {days}d
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border rounded px-2 py-1 bg-white"
            data-testid="price-history-window-select"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchHistory}
            disabled={loading}
            data-testid="price-history-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !data || data.history_count === 0 ? (
          <p className="text-sm text-gray-500" data-testid="price-history-empty">
            No price changes recorded in the last {days} days.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="price-history-table">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-3 font-medium text-gray-600">Date</th>
                  <th className="py-2 pr-3 font-medium text-gray-600">Source</th>
                  <th className="py-2 pr-3 font-medium text-gray-600 text-right">Base</th>
                  <th className="py-2 pr-3 font-medium text-gray-600 text-right">Customer</th>
                  <th className="py-2 pr-3 font-medium text-gray-600 text-right">MRP</th>
                  <th className="py-2 pr-3 font-medium text-gray-600 text-right">Δ</th>
                  <th className="py-2 font-medium text-gray-600">By</th>
                </tr>
              </thead>
              <tbody>
                {(data.history || []).map((row) => (
                  <tr
                    key={row.id}
                    className="border-b last:border-0 hover:bg-gray-50"
                    data-testid={`price-history-row-${row.id}`}
                  >
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">
                      {fmtDate(row.changed_at)}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="text-xs">
                        {SOURCE_LABELS[row.source] || row.source}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <div className="text-gray-500 line-through text-xs">
                        {fmtINR(row.base_price_before)}
                      </div>
                      <div className="font-medium">{fmtINR(row.base_price_after)}</div>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <div className="text-gray-500 text-xs">
                        {fmtINR(row.aniyor_listing_price_before)}
                      </div>
                      <div>{fmtINR(row.aniyor_listing_price_after)}</div>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <div>{fmtINR(row.mrp_price_after)}</div>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <DeltaPill pct={row.delta_pct} />
                    </td>
                    <td className="py-2 text-xs text-gray-600">
                      {row.changed_by_role || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-xs text-gray-500">
              Current base: <strong>{fmtINR(data.current_base_price)}</strong> · Customer-facing:{' '}
              <strong>{fmtINR(data.current_aniyor_listing_price)}</strong>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PriceHistoryPanel;
